<template>
  <v-dialog
    :model-value="show"
    scrollable
    min-width="100px"
    min-height="300px"
    :width="width"
    :height="height"
    :max-width="maxWidth"
    :max-height="maxHeight"
    persistent
    no-click-animation
    transition="dialog-center-transition"
  >
    <v-card>
      <div v-if="banner" class="bg-accent banner">
        <v-img src="/logos/itemis_CREATE_Logo_h_weiss_RGB.svg" alt="Logo" class="logo" />
      </div>

      <v-card-title class="d-flex flex-column px-0">
        <v-btn-icon
          v-if="cancelable"
          data-testid="base-dialog.close-button"
          icon="mdi-close"
          size="x-small"
          class="align-self-end"
          @click="closeButtonAction"
        >
        </v-btn-icon>
        <h4
          :class="[
            'mx-4 mt-2 mb-6 title-container font-weight-bold align-self-center',
            { 'text-error': type === 'delete' },
          ]"
        >
          {{ title || (type === "delete" ? "Delete Confirmation" : "No Title") }}
        </h4>
      </v-card-title>
      <v-card-subtitle v-if="$slots.subHeader" class="pb-2"> <slot name="subHeader"></slot></v-card-subtitle>
      <v-divider v-if="$slots.subHeader"></v-divider>

      <v-card-text class="d-flex flex-column pb-0 pt-2 px-4">
        <slot name="content"></slot>
        <div v-if="type === 'delete'" class="my-4 text-error">This action cannot be undone.</div>
      </v-card-text>
      <v-card-actions :class="['ma-0 px-4 pb-6 pt-4 d-flex', footerSlotClass]">
        <slot name="footer"></slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, useSlots, watch } from "vue";
import { KeyEventBus } from "../event-bus";
import { editorStateStore } from "../Editor/services/storage/editor-state-store";

export interface Props {
  show?: boolean;
  banner?: boolean;
  title?: string;
  cancelable?: boolean;
  dialogId: string;
  width?: string;
  height?: string;
  maxWidth?: string;
  maxHeight?: string;
  type?: "default" | "delete";
}

const compProps = withDefaults(defineProps<Props>(), {
  cancelable: true,
  show: true,
  banner: false,
  title: undefined,
  width: "auto",
  height: "auto",
  maxWidth: undefined,
  maxHeight: undefined,
  type: "default",
});

const emit = defineEmits(["cancelDialog", "completeDialog"]);

const slots = useSlots();

const footerSlotClass = computed(() => {
  const footerSlot = [];
  if (slots.footer) {
    slots.footer({}).forEach((el) => {
      if (typeof el.type.valueOf() === "object") {
        footerSlot.push(el);
      }
    });
  }
  return footerSlot.length > 1 ? "justify-space-between" : "justify-end";
});

const escHandler = () => {
  if (compProps.cancelable && compProps.dialogId === editorStateStore().getTopLayer) {
    closeButtonAction();
  }
};

const enterHandler = () => {
  if (compProps.dialogId === editorStateStore().getTopLayer) {
    emit("completeDialog");
  }
};

watch(
  () => compProps.show,
  (value: boolean) => {
    if (value) {
      editorStateStore().openLayer(compProps.dialogId);
      KeyEventBus.on("esc", escHandler);
      KeyEventBus.on("enter", enterHandler);
    } else {
      editorStateStore().closeLayer(compProps.dialogId);
      KeyEventBus.off("esc", escHandler);
      KeyEventBus.off("enter", enterHandler);
    }
  },
  {
    immediate: true,
  }
);

onBeforeUnmount(() => {
  editorStateStore().closeLayer(compProps.dialogId);
  KeyEventBus.off("esc", escHandler);
  KeyEventBus.off("enter", enterHandler);
});

function closeButtonAction() {
  emit("cancelDialog");
}
</script>

<style scoped>
.v-card-subtitle {
  opacity: 1;
}
.banner {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}
h4 {
  font-size: 25px;
  font-weight: 400;
  color: rgba(var(--v-theme-font));
}
.logo {
  width: 100%;
  height: 100px;
  margin: 25px;
}
</style>

<style>
.base-dialog-action-button {
  flex-grow: 1;
  max-width: min(48%, 200px);
}
</style>
